import { DEFAULT_CALENDAR_PREFERENCES } from '@/app/(main)/calendar/consts/calendar-user-settings';
import type { CalendarPreferences, CalendarViewMode, CalendarVisibilityState } from '@/types/calendarTypes';
import type { CalendarUserSettingsApi, CalendarUserSettingsViewApi } from '@/types/calendarUserSettingsApiTypes';

const VIEW_MODE_TO_API: Record<CalendarViewMode, CalendarUserSettingsViewApi> = {
  day: 'DAILY',
  week: 'WEEKLY',
  month: 'MONTHLY',
};

const API_VIEW_TO_MODE: Record<CalendarUserSettingsViewApi, CalendarViewMode> = {
  DAILY: 'day',
  WEEKLY: 'week',
  MONTHLY: 'month',
};

function isCalendarUserSettingsViewApi(value: unknown): value is CalendarUserSettingsViewApi {
  return value === 'DAILY' || value === 'WEEKLY' || value === 'MONTHLY';
}

function parseVisibility(value: unknown): CalendarVisibilityState {
  if (typeof value !== 'object' || value === null) {
    return DEFAULT_CALENDAR_PREFERENCES.visibility;
  }

  const record = value as Record<string, unknown>;

  return {
    events: typeof record.events === 'boolean' ? record.events : DEFAULT_CALENDAR_PREFERENCES.visibility.events,
    birthdays:
      typeof record.birthdays === 'boolean' ? record.birthdays : DEFAULT_CALENDAR_PREFERENCES.visibility.birthdays,
    tasks: typeof record.tasks === 'boolean' ? record.tasks : DEFAULT_CALENDAR_PREFERENCES.visibility.tasks,
  };
}

export function mapCalendarPreferencesToUserSettingsApi(preferences: CalendarPreferences): CalendarUserSettingsApi {
  return {
    events: preferences.visibility.events,
    birthdays: preferences.visibility.birthdays,
    tasks: preferences.visibility.tasks,
    view: VIEW_MODE_TO_API[preferences.view],
  };
}

export function mapUserSettingsApiToCalendarPreferences(settings: unknown): CalendarPreferences {
  if (typeof settings !== 'object' || settings === null) {
    return DEFAULT_CALENDAR_PREFERENCES;
  }

  const record = settings as Record<string, unknown>;
  const visibility = parseVisibility(record);

  const view = isCalendarUserSettingsViewApi(record.view)
    ? API_VIEW_TO_MODE[record.view]
    : DEFAULT_CALENDAR_PREFERENCES.view;

  return { view, visibility };
}

export function unwrapCalendarUserSettingsResponse(data: unknown): CalendarPreferences {
  if (typeof data !== 'object' || data === null) {
    return DEFAULT_CALENDAR_PREFERENCES;
  }

  const record = data as Record<string, unknown>;

  if ('settings' in record) {
    return mapUserSettingsApiToCalendarPreferences(record.settings);
  }

  return mapUserSettingsApiToCalendarPreferences(record);
}
